import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Badge, Menu } from 'antd';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  CarOutlined,
  InboxOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BellOutlined,
  ShopOutlined,
  CheckSquareOutlined,
  MailOutlined,
  FolderOpenOutlined,
  WarningOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { useBranding } from '../hooks/useBranding';
import { formatAppVersion } from '../utils/appVersion';
import { AUTH_USER_CHANGED_EVENT, authService } from '../services/auth';
import {
  canViewProductNavigation,
  canViewUserManagementNavigation,
} from '../modules/navigation/navigationAccessPolicy';
import { useCurrentPageHeaderTitleOverride } from '../modules/navigation/pageHeaderTitleOverride';
import { canViewGlobalCommunications } from '../modules/notifications/communicationsAccessPolicy';
import { isTesterMode } from '../modules/tester/testerModeAccessPolicy';
import { canReadTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import {
  SALES_REP_HOME_PATH,
  isSalesRep,
  isSalesRepAppPathAllowed,
} from '../modules/salesRep/salesRepAccessPolicy';
import { canViewFactories } from '../modules/factories/factoryAccessPolicy';
import { canReadInternalTasks } from '../modules/tasks/internalTaskAccessPolicy';
import { useMyOpenTaskCount } from '../modules/tasks/useMyOpenTaskCount';
import { MARKETING_HOME_PATH, isMarketing, isMarketingAppPathAllowed } from '../modules/marketing/marketingAccessPolicy';
import AppHeader from './AppHeader';
import ReleaseNotesVersionButton from '../modules/releaseNotes/components/ReleaseNotesVersionButton';
import { useThemeMode } from '../theme/ThemeModeContext';

const { Content, Sider } = AntLayout;

// Maps a route pathname to the menu i18n key shown as the page title in the
// header. Ordered most-specific-first so the longest matching prefix wins
// (e.g. /app/inventory/reports must be checked before /app/inventory).
// „Przyjęcia i wydania” opens the PZ list; the WZ list is its second tab, so
// both list routes highlight the same sidebar item.
const WAREHOUSE_DOCUMENTS_MENU_KEY = '/app/pz';
const isWarehouseDocumentsPath = (pathname: string) => pathname === '/app/pz' || pathname === '/app/wz';

// Keep in sync with the app routes (App.tsx) and the sidebar menuItems below
// when adding a section; an unmapped route falls back to the generic appTitle.
const PAGE_TITLE_KEYS: ReadonlyArray<readonly [string, string]> = [
  ['/app/dashboard', 'menu.dashboard'],
  ['/app/tasks', 'menu.tasks'],
  ['/app/orders', 'menu.orders'],
  ['/app/supplier-orders', 'menu.orders'],
  ['/app/customers', 'menu.customers'],
  ['/app/suppliers', 'menu.suppliers'],
  ['/app/factories', 'menu.factories'],
  ['/app/transports', 'menu.transports'],
  ['/app/reports', 'menu.profitability'],
  ['/app/documents', 'menu.documents'],
  ['/app/wz', 'menu.wzDocuments'],
  ['/app/invoices', 'menu.invoices'],
  ['/app/purchase-invoices', 'menu.purchaseInvoices'],
  ['/app/sales-invoices', 'menu.salesInvoices'],
  ['/app/pz', 'menu.pzDocuments'],
  ['/app/products', 'menu.products'],
  ['/app/inventory/reports', 'menu.inventoryReports'],
  ['/app/inventory/batch-ledger', 'menu.batchLedger'],
  ['/app/inventory/batch-profitability', 'menu.batchProfitability'],
  ['/app/inventory', 'menu.inventory'],
  ['/app/profitability', 'menu.profitability'],
  ['/app/settings/notifications', 'menu.notificationSettings'],
  ['/app/settings', 'menu.settings'],
  ['/app/admin/users', 'menu.userManagement'],
  ['/app/admin/exceptions', 'menu.operationalExceptions'],
  ['/app/communications', 'menu.communications'],
];

const resolvePageTitleKey = (pathname: string): string | null => {
  const match = PAGE_TITLE_KEYS.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match ? match[1] : null;
};

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();
  const { branding } = useBranding();
  const { mode, toggleMode } = useThemeMode();
  const [, setAuthRevision] = useState(0);
  // Collapsible sidebar: when collapsed the Sider shrinks to width 0 (fully
  // hidden) so table pages span the full page width. State lives here (Layout
  // is not remounted on in-app tab navigation) and resets on full refresh.
  // The header toggle button drives this manually; the Sider's `onBreakpoint`
  // also re-syncs it whenever the viewport crosses the `md` (768px) boundary,
  // which intentionally overrides a manual toggle on resize (collapse on
  // entering mobile, expand on returning to desktop).
  const [collapsed, setCollapsed] = useState(false);
  const user = authService.getCurrentUser();

  useEffect(() => {
    const handleAuthUserChanged = () => setAuthRevision((revision) => revision + 1);
    window.addEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
  }, []);

  const handleLogout = () => {
    authService.logout();
  };

  const isTester = isTesterMode(user);
  const isSalesRepUser = isSalesRep(user);
  const isMarketingUser = isMarketing(user);
  const canViewReports = authService.canViewFinancialReports();
  const canViewWarehouse = authService.canViewWarehouseDocuments();
  const canViewProducts = canViewProductNavigation(user);
  const canViewDocumentCenter = authService.canViewDocumentCenter();
  const canViewCommunications = canViewGlobalCommunications(user);
  const canViewUserManagement = canViewUserManagementNavigation(user);
  const canViewTransports = canReadTransportRoutes(user);
  const canViewFactoryList = canViewFactories(user);
  const isDashboard = location.pathname === '/app/dashboard';
  const pageTitleKey = resolvePageTitleKey(location.pathname);
  const pageTitleOverride = useCurrentPageHeaderTitleOverride();
  // Only the full menu has „Zadania”; TESTER, Handlowiec and MARKETING menus do not.
  const showsTasksMenu = !isTester && !isSalesRepUser && !isMarketingUser && canReadInternalTasks(user);
  const myOpenTaskCount = useMyOpenTaskCount(showsTasksMenu, location.pathname);

  // Handlowiec (ORDER_STAFF) works only with his orders and master data; the
  // backend denies every other API (utils/salesRepPolicy.js), so the menu and
  // the route guard below mirror that list instead of the capability menu.
  const salesRepMenuItems = [
    {
      type: 'group' as const,
      label: t('menu.sections.orders'),
      children: [
        {
          key: '/app/orders',
          icon: <ShoppingCartOutlined />,
          label: t('menu.orders'),
        },
        {
          key: '/app/customers',
          icon: <TeamOutlined />,
          label: t('menu.customers'),
        },
        {
          key: '/app/suppliers',
          icon: <ShopOutlined />,
          label: t('menu.suppliers'),
        },
        {
          key: '/app/factories',
          icon: <BankOutlined />,
          label: t('menu.factories'),
        },
      ],
    },
    {
      type: 'group' as const,
      label: t('menu.sections.catalog'),
      children: [{
        key: '/app/products',
        icon: <AppstoreOutlined />,
        label: t('menu.products'),
      }],
    },
  ];

  // TESTER menu is restricted to Dashboard/Orders/Customers only — no
  // Warehouse, Reports, Invoices, Transports, WZ, Settings, or Notifications
  // (see AGENTS.md "TESTER Role (hidden)"). All other roles keep the full
  // capability-gated menu below.
  const marketingMenuItems = [
    { key: '/app/customers', icon: <TeamOutlined />, label: t('menu.customers') },
    { key: '/app/suppliers', icon: <ShopOutlined />, label: t('menu.suppliers') },
    { key: '/app/factories', icon: <BankOutlined />, label: t('menu.factories') },
  ];
  const menuItems = isMarketingUser ? marketingMenuItems : isSalesRepUser ? salesRepMenuItems : isTester ? [
    {
      type: 'group' as const,
      label: t('menu.sections.dailyWork'),
      children: [{
        key: '/app/dashboard',
        icon: <DashboardOutlined />,
        label: t('menu.dashboard'),
      }],
    },
    {
      type: 'group' as const,
      label: t('menu.sections.orders'),
      children: [
        {
          key: '/app/orders',
          icon: <ShoppingCartOutlined />,
          label: t('menu.orders'),
        },
        {
          key: '/app/customers',
          icon: <TeamOutlined />,
          label: t('menu.customers'),
        },
      ],
    },
  ] : [
    {
      type: 'group' as const,
      label: t('menu.sections.dailyWork'),
      children: [
        {
          key: '/app/dashboard',
          icon: <DashboardOutlined />,
          label: t('menu.dashboard'),
        },
        {
          key: '/app/tasks',
          icon: <CheckSquareOutlined />,
          label: (
            <span className="app-menu-badge-label">
              {t('menu.tasks')}
              <Badge
                count={myOpenTaskCount}
                overflowCount={99}
                size="small"
                title={t('menu.tasksOpenBadge', { count: myOpenTaskCount })}
              />
            </span>
          ),
        },
        ...(canViewCommunications ? [{
          key: '/app/communications',
          icon: <MailOutlined />,
          label: t('menu.communications'),
        }] : []),
      ],
    },
    {
      type: 'group' as const,
      label: t('menu.sections.orders'),
      children: [
        {
          key: '/app/orders',
          icon: <ShoppingCartOutlined />,
          label: t('menu.orders'),
        },
        {
          key: '/app/customers',
          icon: <TeamOutlined />,
          label: t('menu.customers'),
        },
        ...(canViewWarehouse ? [{
          key: '/app/suppliers',
          icon: <ShopOutlined />,
          label: t('menu.suppliers'),
        }] : []),
        ...(canViewFactoryList ? [{
          key: '/app/factories',
          icon: <BankOutlined />,
          label: t('menu.factories'),
        }] : []),
      ],
    },
    {
      type: 'group' as const,
      label: t('menu.sections.logistics'),
      children: [
        ...(canViewTransports ? [{
          key: '/app/transports',
          icon: <CarOutlined />,
          label: t('menu.transports'),
        }] : []),
        // One item for both warehouse document lists (decision 2026-10-14);
        // PZ and WZ are tabs inside it. A role without warehouse documents
        // keeps the WZ list only, as before.
        canViewWarehouse ? {
          key: WAREHOUSE_DOCUMENTS_MENU_KEY,
          icon: <FileTextOutlined />,
          label: t('menu.warehouseDocuments'),
        } : {
          key: '/app/wz',
          icon: <FileTextOutlined />,
          label: t('menu.wzDocuments'),
        },
        ...(canViewWarehouse ? [
          {
            key: '/app/inventory',
            icon: <InboxOutlined />,
            label: t('menu.inventory'),
          },
        ] : []),
      ],
    },
    ...(canViewProducts ? [{
      type: 'group' as const,
      label: t('menu.sections.catalog'),
      children: [{
        key: '/app/products',
        icon: <AppstoreOutlined />,
        label: t('menu.products'),
      }],
    }] : []),
    ...((canViewDocumentCenter || canViewReports) ? [{
      type: 'group' as const,
      label: t('menu.sections.documentsAndReports'),
      children: [
        ...(canViewDocumentCenter ? [{
          key: '/app/documents',
          icon: <FolderOpenOutlined />,
          label: t('menu.documents'),
        }] : []),
        ...(canViewReports ? [
          {
            key: '/app/profitability',
            icon: <BarChartOutlined />,
            label: t('menu.profitability'),
          },
        ] : []),
      ],
    }] : []),
    ...(canViewUserManagement ? [{
      type: 'group' as const,
      label: t('menu.sections.administration'),
      children: [{
        key: '/app/admin/users',
        icon: <TeamOutlined />,
        label: t('menu.userManagement'),
      }, {
        key: '/app/admin/exceptions',
        icon: <WarningOutlined />,
        label: t('menu.operationalExceptions'),
      }],
    }] : []),
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: user?.name || 'Profile',
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    // Settings/notifications are hidden for TESTER — its menu is limited to
    // Dashboard/Orders/Customers and it has no settings surface to manage.
    ...(isTester ? [] : [
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: t('menu.settings'),
        onClick: () => navigate('/app/settings'),
      },
      ...((isSalesRepUser || isMarketingUser) ? [] : [{
        key: 'notifications',
        icon: <BellOutlined />,
        label: t('menu.notificationSettings'),
        onClick: () => navigate('/app/settings/notifications'),
      }]),
      {
        type: 'divider' as const,
      },
    ]),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('logout'),
      onClick: handleLogout,
    },
  ];

  if (isMarketingUser && !isMarketingAppPathAllowed(location.pathname)) {
    return <Navigate to={MARKETING_HOME_PATH} replace />;
  }

  if (isSalesRepUser && !isSalesRepAppPathAllowed(location.pathname)) {
    return <Navigate to={SALES_REP_HOME_PATH} replace />;
  }

  const appVersion = process.env.REACT_APP_VERSION || 'dev';
  const gitSha = process.env.REACT_APP_GIT_SHA || '';
  const displayedVersion = formatAppVersion(appVersion, gitSha);

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        width={200}
        theme="dark"
        collapsible
        collapsed={collapsed}
        collapsedWidth={0}
        trigger={null}
        breakpoint="md"
        onBreakpoint={(broken) => setCollapsed(broken)}
        className="app-sidebar"
        style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', alignSelf: 'flex-start' }}
      >
        <div style={{ height: 48, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            aria-label="Azzura"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minWidth: 0
            }}
          >
            {branding.logoUrl && (
              <img
                src={branding.logoUrl}
                alt=""
                aria-hidden="true"
                style={{ width: 34, height: 34, objectFit: 'contain', flex: '0 0 auto' }}
              />
            )}
            <span
              style={{
                color: '#fff',
                fontSize: 24,
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: 0
              }}
            >
              Azzura
            </span>
          </div>
        </div>
        <Menu
          className="app-sidebar-menu"
          theme="dark"
          mode="inline"
          selectedKeys={[canViewWarehouse && isWarehouseDocumentsPath(location.pathname) ? WAREHOUSE_DOCUMENTS_MENU_KEY : location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
        <div style={{ padding: '6px 24px', color: '#555', fontSize: 11, flex: '0 0 auto' }}>
          <ReleaseNotesVersionButton version={displayedVersion} />
        </div>
      </Sider>
      <AntLayout>
        <AppHeader
          collapsed={collapsed}
          pageTitle={pageTitleOverride ?? t(pageTitleKey ?? 'appTitle')}
          language={currentLanguage}
          languageLabel={t('language')}
          languages={availableLanguages.map(lang => ({
            value: lang.code,
            label: lang.name,
          }))}
          roleLabel={`${t('role')}: ${t(`userRole.${user?.role}`)}`}
          userName={user?.name || ''}
          userMenuItems={userMenuItems}
          toggleSidebarLabel={t(collapsed ? 'expandSidebar' : 'collapseSidebar')}
          themeMode={mode}
          themeToggleLabel={t(mode === 'dark' ? 'theme.useLight' : 'theme.useDark')}
          onLanguageChange={changeLanguage}
          onToggleTheme={toggleMode}
          onToggleSidebar={() => setCollapsed((value) => !value)}
        />
        <Content
          className={`app-content${isDashboard ? ' app-content--dashboard' : ''}`}
        >
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
